import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../lib/supabase";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDateNumeric } from "@/lib/date-format";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Search,
  Upload,
  Download,
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  Trash2,
  X,
  Loader2,
  Image as ImageIcon,
  File,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { resolveTenantDocumentUrl } from "@/lib/tenant-document-url";
import { generateStorageFileName } from "@/lib/document-storage-filename";
import { notifyTenantDocumentStatusEmail } from "@/lib/notify-tenant-document-email";
import { operatorStamped } from "@/lib/operator-stamped";

interface AdminDocumentsProps {
  showScreening?: boolean;
}

interface DocumentFile {
  id: string;
  file: File;
  title: string;
  documentType: string;
  preview?: string;
}

export const AdminDocuments = ({
  showScreening = false,
}: AdminDocumentsProps) => {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "pending" | "approved" | "rejected"
  >("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [documents, setDocuments] = useState<DocumentFile[]>([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<any>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingDocumentId, setRejectingDocumentId] = useState<string | null>(
    null
  );
  const [rejectingTenantUserId, setRejectingTenantUserId] = useState<
    string | null
  >(null);
  const [rejectingDocMeta, setRejectingDocMeta] = useState<{
    title?: string | null;
    documentType?: string | null;
    tenantDisplayName?: string | null;
  } | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [uploadTargetUserId, setUploadTargetUserId] = useState<string>("");
  const [rejectionDetailsOpen, setRejectionDetailsOpen] = useState(false);
  const [selectedRejectedDocument, setSelectedRejectedDocument] =
    useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, typeFilter, search]);

  // Fetch documents from database - SIMPLIFIED QUERY FIRST
  const {
    data: documentsData,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ["admin-documents", statusFilter, typeFilter],
    queryFn: async () => {
      try {
        console.log("🔍 Fetching documents with filters:", {
          statusFilter,
          typeFilter,
        });

        // Start with a simple query first
        let query = supabase.from("tenant_documents").select("*");

        // Apply filters
        if (statusFilter !== "all") {
          query = query.eq("status", statusFilter);
        }
        if (typeFilter !== "all") {
          query = query.eq("document_type", typeFilter);
        }

        // Apply ordering
        query = query.order("created_at", { ascending: false });

        const { data, error } = await query;

        if (error) {
          console.error("❌ Documents query error:", error);
          throw error;
        }

        console.log("✅ Documents fetched:", data?.length || 0, "documents");

        // If we have data, fetch related profiles and bookings separately
        if (data && data.length > 0) {
          const userIds = [
            ...new Set(data.map((doc: any) => doc.user_id).filter(Boolean)),
          ];
          const bookingIds = [
            ...new Set(data.map((doc: any) => doc.booking_id).filter(Boolean)),
          ];

          // Fetch profiles
          const profilesMap: Record<string, any> = {};
          if (userIds.length > 0) {
            const { data: profiles, error: profilesError } = await supabase
              .from("profiles")
              .select("id, name, email")
              .in("id", userIds);

            if (!profilesError && profiles) {
              profiles.forEach((profile: any) => {
                profilesMap[profile.id] = profile;
              });
            }
          }

          // Fetch bookings
          const bookingsMap: Record<string, any> = {};
          if (bookingIds.length > 0) {
            const { data: bookings, error: bookingsError } = await supabase
              .from("bookings")
              .select("id, start_date, end_date")
              .in("id", bookingIds);

            if (!bookingsError && bookings) {
              bookings.forEach((booking: any) => {
                bookingsMap[booking.id] = booking;
              });
            }
          }

          // Attach related data
          const enrichedData = data.map((doc: any) => ({
            ...doc,
            profiles: profilesMap[doc.user_id] || null,
            bookings: bookingsMap[doc.booking_id] || null,
          }));

          return enrichedData;
        }

        return data || [];
      } catch (error: any) {
        console.error("❌ Error fetching documents:", error);
        toast.error(
          `${t("admin.documents.error.fetchFailed", "Failed to fetch documents")}: ${error.message}`
        );
        throw error;
      }
    },
  });

  // Debug: Log when documentsData changes
  useEffect(() => {
    console.log("📊 Documents data updated:", {
      count: documentsData?.length || 0,
      statusFilter,
      typeFilter,
      isLoading,
      queryError,
    });
  }, [documentsData, statusFilter, typeFilter, isLoading, queryError]);

  const { data: profilesForUpload } = useQuery({
    queryKey: ["admin-profiles-document-upload"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, name, email")
        .order("created_at", { ascending: false })
        .limit(400);
      if (error) throw error;
      return data || [];
    },
  });

  // Fetch screening data if needed
  const { data: screeningData } = useQuery({
    queryKey: ["admin-screening"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenant_screening")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: showScreening,
  });

  const updateDocumentStatus = useMutation({
    mutationFn: async ({
      id,
      status,
      notes,
      tenantUserId,
      docTitle,
      docDocumentType,
      tenantDisplayName,
    }: {
      id: string;
      status: string;
      notes?: string;
      tenantUserId?: string | null;
      docTitle?: string | null;
      docDocumentType?: string | null;
      tenantDisplayName?: string | null;
    }) => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("tenant_documents")
        .update({
          status,
          reviewed_at: new Date().toISOString(),
          reviewed_by: user?.id || null,
          notes: notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;
      return {
        tenantUserId: tenantUserId ?? null,
        status,
        docTitle,
        docDocumentType,
        notes,
        tenantDisplayName: tenantDisplayName ?? null,
        id,
      };
    },
    onSuccess: async (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
      refetch();
      toast.success(
        t("admin.documents.success.statusUpdated", "Document status updated")
      );
      setRejectDialogOpen(false);
      setRejectionReason("");
      setRejectingDocumentId(null);
      setRejectingTenantUserId(null);
      setRejectingDocMeta(null);
      if (result.status === "approved" || result.status === "rejected") {
        await notifyTenantDocumentStatusEmail(
          result.tenantUserId,
          result.status as "approved" | "rejected",
          {
            tenantName: result.tenantDisplayName,
            documentId: result.id,
            documents: [
              {
                title: result.docTitle,
                documentType: result.docDocumentType,
                status: result.status as "approved" | "rejected",
              },
            ],
            rejectionNotes:
              result.status === "rejected" ? result.notes : undefined,
          }
        );
      }
    },
    onError: (error: any) => {
      toast.error(
        error.message ||
          t("admin.documents.error.updateFailed", "Failed to update document")
      );
    },
  });

  // Upload documents mutation
  const uploadDocumentsMutation = useMutation({
    mutationFn: async (docs: DocumentFile[]) => {
      const uploadedDocuments = [];

      for (const doc of docs) {
        // Validate file
        if (doc.file.size > 10 * 1024 * 1024) {
          throw new Error(
            `${doc.file.name} ${t("admin.documents.error.tooLarge", "exceeds 10MB limit")}`
          );
        }

        // Validate document type
        if (!doc.documentType) {
          throw new Error(
            `${t("admin.documents.error.selectTypeFor", "Please select a document type for")} ${doc.file.name}`
          );
        }

        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user)
          throw new Error(
            t(
              "admin.documents.error.notAuthenticated",
              "User not authenticated"
            )
          );
        if (!uploadTargetUserId) {
          throw new Error(
            t(
              "admin.documents.error.selectTenantFirst",
              "Select a tenant before uploading"
            )
          );
        }

        const storageFileName = generateStorageFileName(
          doc.file.name,
          uploadTargetUserId
        );

        // Upload to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("contracts")
          .upload(storageFileName, doc.file, {
            contentType: doc.file.type,
            upsert: false,
          });

        if (uploadError) {
          console.error("Upload error:", uploadError);
          throw new Error(
            `${t("admin.documents.error.uploadFailed", "Failed to upload file")}: ${uploadError.message}`
          );
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from("contracts")
          .getPublicUrl(storageFileName);

        // Get current timestamp for created_at and updated_at
        const now = new Date().toISOString();

        // Insert document record with pending status
        const { data: docData, error: insertError } = await supabase
          .from("tenant_documents")
          .insert(
            operatorStamped<"tenant_documents">({
              user_id: uploadTargetUserId,
              document_type: doc.documentType,
              title: doc.title || doc.file.name,
              file_url: urlData.publicUrl,
              file_name: doc.file.name,
              file_size: doc.file.size,
              mime_type: doc.file.type,
              uploaded_by: user.id,
              // Uploaded from the admin area on the tenant's behalf.
              source: "admin",
              status: "pending",
              created_at: now,
              updated_at: now,
            })
          )
          .select()
          .single();

        if (insertError) {
          console.error("Insert error:", insertError);
          throw new Error(
            `${t("admin.documents.error.saveFailed", "Failed to save document record")}: ${insertError.message}`
          );
        }

        // Best-effort audit log for tenant activity timeline
        try {
          const { error: auditError } = await supabase
            .from("audit_logs")
            .insert({
              action: "document_uploaded_admin",
              resource_type: "document",
              resource_id: String(docData?.id ?? ""),
              user_id: user.id,
              created_at: now,
              metadata: {
                target_user_id: uploadTargetUserId,
                document_type: doc.documentType,
                title: doc.title || doc.file.name,
                manual_admin_upload: true,
              },
              new_values: {
                document_type: doc.documentType,
                title: doc.title || doc.file.name,
                file_name: doc.file.name,
                file_url: urlData.publicUrl,
                status: "pending",
              },
            });
          if (auditError) {
            console.warn(
              "Failed to write audit log for document upload",
              auditError
            );
          }
        } catch (e) {
          console.warn("Audit log insert threw for document upload", e);
        }

        console.log("✅ Document uploaded successfully:", docData);
        uploadedDocuments.push(docData);
      }

      return uploadedDocuments;
    },
    onSuccess: (uploadedDocs) => {
      console.log("✅ All documents uploaded:", uploadedDocs.length);
      // Reset filters to show all documents including the new ones
      setStatusFilter("all");
      setTypeFilter("all");
      setCurrentPage(1); // Reset to first page
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["admin-documents"] });
      // Wait a bit then refetch to ensure data is available
      setTimeout(() => {
        refetch();
      }, 500);
      toast.success(
        `${t("admin.documents.success.uploaded", "Successfully uploaded")} ${uploadedDocs.length} ${t("admin.documents.success.uploadedSuffix", "document(s)")}`
      );
      handleCancelAll();
    },
    onError: (error: any) => {
      console.error("Upload mutation error:", error);
      toast.error(
        error.message ||
          t(
            "admin.documents.error.uploadDocumentsFailed",
            "Failed to upload documents"
          )
      );
    },
  });

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newDocs: DocumentFile[] = Array.from(files).map((file) => {
      const id = `${Date.now()}-${Math.random()}`;
      const preview = file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : undefined;

      return {
        id,
        file,
        title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension for default title
        documentType: "", // Initialize empty
        preview,
      };
    });

    setDocuments((prev) => [...prev, ...newDocs]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleRemoveDocument = (id: string) => {
    setDocuments((prev) => {
      const doc = prev.find((d) => d.id === id);
      if (doc?.preview) {
        URL.revokeObjectURL(doc.preview);
      }
      return prev.filter((d) => d.id !== id);
    });
  };

  const handleUpdateDocument = (id: string, updates: Partial<DocumentFile>) => {
    setDocuments((prev) =>
      prev.map((doc) => (doc.id === id ? { ...doc, ...updates } : doc))
    );
  };

  const handleCancelAll = () => {
    documents.forEach((doc) => {
      if (doc.preview) {
        URL.revokeObjectURL(doc.preview);
      }
    });
    setDocuments([]);
    setUploadDialogOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = () => {
    if (documents.length === 0) {
      toast.error(
        t(
          "admin.documents.error.selectOneFile",
          "Please select at least one file"
        )
      );
      return;
    }
    if (!uploadTargetUserId) {
      toast.error(
        t(
          "admin.documents.error.selectTenant",
          "Please select the tenant this upload is for"
        )
      );
      return;
    }

    // Validate all documents have a type selected
    const invalidDocs = documents.filter((doc) => !doc.documentType);
    if (invalidDocs.length > 0) {
      toast.error(
        t(
          "admin.documents.error.selectTypeAll",
          "Please select a document type for all documents"
        )
      );
      return;
    }

    uploadDocumentsMutation.mutate(documents);
  };

  // file_url is the public URL stored at upload (or a Storage path). The
  // viewer gets a short-lived signed URL instead: the bucket is going private
  // (COL-436).
  const handleViewDocument = async (doc: any) => {
    const signed = await resolveTenantDocumentUrl(doc.file_url);
    if (!signed) {
      toast.error(t("admin.docs.openFailed", "Could not open the document."));
      return;
    }
    setViewingDocument({ ...doc, file_url: signed });
    setViewDialogOpen(true);
  };

  const handleApproveDocument = (doc: {
    id: string;
    user_id?: string | null;
    title?: string | null;
    document_type?: string | null;
    profiles?: { name?: string | null };
  }) => {
    updateDocumentStatus.mutate({
      id: doc.id,
      status: "approved",
      tenantUserId: doc.user_id,
      docTitle: doc.title,
      docDocumentType: doc.document_type,
      tenantDisplayName: doc.profiles?.name,
    });
  };

  const handleRejectDocument = (doc: {
    id: string;
    user_id?: string | null;
    title?: string | null;
    document_type?: string | null;
    profiles?: { name?: string | null };
  }) => {
    setRejectingDocumentId(doc.id);
    setRejectingTenantUserId(doc.user_id ?? null);
    setRejectingDocMeta({
      title: doc.title,
      documentType: doc.document_type,
      tenantDisplayName: doc.profiles?.name,
    });
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleSubmitRejection = () => {
    if (!rejectingDocumentId) return;

    if (!rejectionReason.trim()) {
      toast.error(
        t(
          "admin.documents.error.rejectionReasonRequired",
          "Please provide a reason for rejection"
        )
      );
      return;
    }

    updateDocumentStatus.mutate({
      id: rejectingDocumentId,
      status: "rejected",
      notes: rejectionReason.trim(),
      tenantUserId: rejectingTenantUserId,
      docTitle: rejectingDocMeta?.title,
      docDocumentType: rejectingDocMeta?.documentType,
      tenantDisplayName: rejectingDocMeta?.tenantDisplayName,
    });
  };

  const handleViewRejectionDetails = (doc: any) => {
    setSelectedRejectedDocument(doc);
    setRejectionDetailsOpen(true);
  };

  const getFileType = (mimeType: string | null, fileName: string | null) => {
    if (!mimeType && !fileName) return "unknown";
    if (mimeType?.startsWith("image/")) return "image";
    if (mimeType === "application/pdf" || fileName?.endsWith(".pdf"))
      return "pdf";
    return "other";
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith("image/"))
      return <ImageIcon className="w-5 h-5" />;
    if (file.type === "application/pdf" || file.name.endsWith(".pdf"))
      return <FileText className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return t("common.notAvailable", "N/A");
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredDocuments = useMemo(() => {
    if (!documentsData) {
      console.log("⚠️ No documentsData available");
      return [];
    }

    const filtered = documentsData.filter((doc: any) => {
      const matchesSearch =
        !search ||
        doc.title?.toLowerCase().includes(search.toLowerCase()) ||
        doc.profiles?.name?.toLowerCase().includes(search.toLowerCase()) ||
        doc.profiles?.email?.toLowerCase().includes(search.toLowerCase());
      return matchesSearch;
    });

    console.log(
      "🔍 Filtered documents:",
      filtered.length,
      "out of",
      documentsData.length
    );
    return filtered;
  }, [documentsData, search]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredDocuments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDocuments = filteredDocuments.slice(startIndex, endIndex);

  // Adjust current page if it's out of bounds
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getStatusBadge = (status: string, doc: any) => {
    const isManualAdminUpload =
      !!doc?.uploaded_by &&
      !!doc?.user_id &&
      String(doc.uploaded_by) !== String(doc.user_id);

    const variants: Record<string, any> = {
      approved: (
        <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 cursor-default">
          {t("admin.documents.status.approved", "Approved")}
        </Badge>
      ),
      rejected: (
        <Badge
          className="bg-destructive/10 text-destructive border-destructive/20 cursor-pointer hover:bg-destructive/20 transition-colors"
          onClick={() => doc.notes && handleViewRejectionDetails(doc)}
        >
          {t("admin.documents.status.rejected", "Rejected")}
        </Badge>
      ),
      pending: (
        <Badge className="bg-amber-50 text-amber-700 border-amber-200 cursor-default">
          {t("admin.documents.status.pending", "Pending")}
        </Badge>
      ),
      expired: (
        <Badge variant="secondary" className="cursor-default">
          {t("admin.documents.status.expired", "Expired")}
        </Badge>
      ),
    };
    const statusBadge = variants[status] || (
      <Badge variant="secondary">{status}</Badge>
    );
    if (!isManualAdminUpload) return statusBadge;
    return (
      <div className="flex items-center gap-2">
        <Badge className="bg-sky-50 text-sky-700 border-sky-200 cursor-default">
          {t("admin.documents.status.manualUpload", "Manual upload by admin")}
        </Badge>
        {statusBadge}
      </div>
    );
  };

  if (showScreening) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            {t("admin.documents.screening.title", "Tenant Screening")}
          </CardTitle>
          <CardDescription>
            {t(
              "admin.documents.screening.description",
              "Manage tenant screening and verification status"
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {screeningData && screeningData.length > 0 ? (
                screeningData.map((screening: any) => (
                  <Card key={screening.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold">
                            {screening.profiles?.name ||
                              t("admin.documents.screening.unknown", "Unknown")}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {screening.profiles?.email ||
                              t("common.notAvailable", "N/A")}
                          </p>
                        </div>
                        <Badge>{screening.status}</Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "admin.documents.screening.idVerified",
                              "ID Verified"
                            )}
                          </p>
                          <p className="font-semibold">
                            {screening.id_verified
                              ? `✓ ${t("common.yes", "Yes")}`
                              : `✗ ${t("common.no", "No")}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "admin.documents.screening.incomeVerified",
                              "Income Verified"
                            )}
                          </p>
                          <p className="font-semibold">
                            {screening.income_verified
                              ? `✓ ${t("common.yes", "Yes")}`
                              : `✗ ${t("common.no", "No")}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "admin.documents.screening.employmentVerified",
                              "Employment Verified"
                            )}
                          </p>
                          <p className="font-semibold">
                            {screening.employment_verified
                              ? `✓ ${t("common.yes", "Yes")}`
                              : `✗ ${t("common.no", "No")}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "admin.documents.screening.referenceChecked",
                              "Reference Checked"
                            )}
                          </p>
                          <p className="font-semibold">
                            {screening.reference_checked
                              ? `✓ ${t("common.yes", "Yes")}`
                              : `✗ ${t("common.no", "No")}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "admin.documents.screening.backgroundCheck",
                              "Background Check"
                            )}
                          </p>
                          <p className="font-semibold">
                            {screening.background_check
                              ? `✓ ${t("common.yes", "Yes")}`
                              : `✗ ${t("common.no", "No")}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "admin.documents.screening.creditCheck",
                              "Credit Check"
                            )}
                          </p>
                          <p className="font-semibold">
                            {screening.credit_check
                              ? `✓ ${t("common.yes", "Yes")}`
                              : `✗ ${t("common.no", "No")}`}
                          </p>
                        </div>
                      </div>
                      {screening.screening_score !== null && (
                        <div className="mt-4">
                          <p className="text-sm text-muted-foreground">
                            {t(
                              "admin.documents.screening.score",
                              "Screening Score"
                            )}
                          </p>
                          <p className="text-2xl font-bold">
                            {screening.screening_score}/100
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  {t(
                    "admin.documents.screening.empty",
                    "No screening records found"
                  )}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100/50 border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                {t("admin.documents.title", "Document Management")}
              </CardTitle>
              <CardDescription className="mt-1.5">
                {t(
                  "admin.documents.subtitle",
                  "Manage tenant documents, contracts, and verification files"
                )}
              </CardDescription>
            </div>
            <Dialog
              open={uploadDialogOpen}
              onOpenChange={(open) => {
                setUploadDialogOpen(open);
                if (!open) setUploadTargetUserId("");
              }}
            >
              <DialogTrigger asChild>
                <Button className="gap-2 shadow-md hover:shadow-lg transition-all duration-200">
                  <Sparkles className="w-4 h-4" />
                  {t("admin.documents.uploadButton", "Upload Documents")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader className="pb-4 border-b">
                  <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-primary" />
                    {t("admin.documents.uploadButton", "Upload Documents")}
                  </DialogTitle>
                  <DialogDescription>
                    {t(
                      "admin.documents.uploadDialog.description",
                      "Choose the tenant account first, then add files (ID, salary statement, contract, etc.)."
                    )}
                  </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto pr-2 space-y-6 py-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      {t(
                        "admin.documents.uploadDialog.tenantLabel",
                        "Tenant *"
                      )}
                    </Label>
                    <Select
                      value={uploadTargetUserId || undefined}
                      onValueChange={setUploadTargetUserId}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={t(
                            "admin.documents.uploadDialog.tenantPlaceholder",
                            "Select tenant (profile)"
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        {(profilesForUpload || []).map(
                          (p: {
                            id: string;
                            name: string | null;
                            email: string | null;
                          }) => (
                            <SelectItem key={p.id} value={p.id}>
                              {(p.name || "—") +
                                (p.email ? ` · ${p.email}` : "")}
                            </SelectItem>
                          )
                        )}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t(
                        "admin.documents.uploadDialog.storageNote",
                        "Files are stored on this tenant's account for review."
                      )}
                    </p>
                  </div>
                  {/* Drag and Drop Area */}
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-300 ${
                      isDragging
                        ? "border-primary bg-primary/5 scale-[1.02]"
                        : "border-slate-300 hover:border-primary hover:bg-slate-50/50"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif"
                      onChange={handleFileInputChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4 shadow-lg">
                        <Upload className="w-8 h-8 text-primary" />
                      </div>
                      <p className="text-lg font-semibold text-slate-900 mb-1">
                        {t(
                          "admin.documents.dropzone.title",
                          "Drop files here or click to browse"
                        )}
                      </p>
                      <p className="text-sm text-slate-500">
                        {t(
                          "admin.documents.dropzone.hint",
                          "PDF, DOC, DOCX, JPG, PNG, GIF, WEBP, HEIC (Max 10MB per file)"
                        )}
                      </p>
                    </label>
                  </div>

                  {/* Document Cards */}
                  {documents.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {t(
                            "admin.documents.selectedCount",
                            "Selected Documents"
                          )}{" "}
                          ({documents.length})
                        </h3>
                      </div>
                      <div className="grid gap-4">
                        {documents.map((doc) => (
                          <Card
                            key={doc.id}
                            className="border-2 border-slate-200 hover:border-primary/50 transition-all duration-200 shadow-sm hover:shadow-md"
                          >
                            <CardContent className="p-5">
                              <div className="flex gap-4">
                                {/* Preview/Icon */}
                                <div className="flex-shrink-0">
                                  {doc.preview ? (
                                    <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-slate-200 bg-slate-50">
                                      <img
                                        src={doc.preview}
                                        alt={doc.title}
                                        className="w-full h-full object-cover"
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-2 border-slate-200">
                                      {getFileIcon(doc.file)}
                                    </div>
                                  )}
                                </div>

                                {/* Document Info */}
                                <div className="flex-1 space-y-3 min-w-0">
                                  <div>
                                    <Label
                                      htmlFor={`type-${doc.id}`}
                                      className="text-xs font-semibold text-slate-600 uppercase tracking-wide"
                                    >
                                      {t(
                                        "admin.documents.docTypeLabel",
                                        "Document Type *"
                                      )}
                                    </Label>
                                    <Select
                                      value={doc.documentType}
                                      onValueChange={(value) =>
                                        handleUpdateDocument(doc.id, {
                                          documentType: value,
                                        })
                                      }
                                    >
                                      <SelectTrigger
                                        id={`type-${doc.id}`}
                                        className="mt-1.5"
                                      >
                                        <SelectValue
                                          placeholder={t(
                                            "admin.documents.docTypePlaceholder",
                                            "Select document type"
                                          )}
                                        />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="contract">
                                          {t(
                                            "admin.documents.docType.contract",
                                            "Contract"
                                          )}
                                        </SelectItem>
                                        <SelectItem value="id_passport">
                                          {t(
                                            "admin.documents.docType.idPassport",
                                            "ID/Passport"
                                          )}
                                        </SelectItem>
                                        <SelectItem value="proof_of_income">
                                          {t(
                                            "admin.documents.docType.proofOfIncome",
                                            "Proof of Income"
                                          )}
                                        </SelectItem>
                                        <SelectItem value="employment_letter">
                                          {t(
                                            "admin.documents.docType.employmentLetter",
                                            "Employment Letter"
                                          )}
                                        </SelectItem>
                                        <SelectItem value="reference">
                                          {t(
                                            "admin.documents.docType.reference",
                                            "Reference"
                                          )}
                                        </SelectItem>
                                        <SelectItem value="other">
                                          {t(
                                            "admin.documents.docType.other",
                                            "Other"
                                          )}
                                        </SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                                    <div className="flex items-center gap-4 text-sm text-slate-600">
                                      <span className="flex items-center gap-1.5">
                                        {getFileIcon(doc.file)}
                                        <span className="font-medium">
                                          {doc.file.name}
                                        </span>
                                      </span>
                                      <span className="text-slate-400">•</span>
                                      <span>
                                        {formatFileSize(doc.file.size)}
                                      </span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const preview =
                                            doc.file.type.startsWith("image/")
                                              ? doc.preview
                                              : doc.file.type ===
                                                  "application/pdf"
                                                ? URL.createObjectURL(doc.file)
                                                : null;
                                          if (preview) {
                                            setViewingDocument({
                                              file_url: preview,
                                              title: doc.title,
                                              mime_type: doc.file.type,
                                              file_name: doc.file.name,
                                            });
                                            setViewDialogOpen(true);
                                          } else {
                                            toast.info(
                                              t(
                                                "admin.documents.previewUnavailable",
                                                "Preview not available for this file type"
                                              )
                                            );
                                          }
                                        }}
                                        className="border-slate-300 hover:bg-primary/10 hover:border-primary hover:text-primary"
                                      >
                                        <Eye className="w-4 h-4 mr-1.5" />
                                        {t("admin.documents.view", "View")}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                          handleRemoveDocument(doc.id)
                                        }
                                        className="border-red-200 hover:bg-red-50 hover:border-red-300 hover:text-red-700"
                                      >
                                        <X className="w-4 h-4 mr-1.5" />
                                        {t("admin.documents.cancel", "Cancel")}
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="border-t pt-4 mt-4">
                  <Button
                    variant="outline"
                    onClick={handleCancelAll}
                    disabled={uploadDocumentsMutation.isPending}
                    className="border-slate-300"
                  >
                    {t("admin.documents.cancelAll", "Cancel All")}
                  </Button>
                  <Button
                    onClick={handleUpload}
                    disabled={
                      uploadDocumentsMutation.isPending ||
                      documents.length === 0
                    }
                    className="shadow-md"
                  >
                    {uploadDocumentsMutation.isPending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t("admin.documents.uploading", "Uploading...")}
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {t("admin.documents.uploadAction", "Upload")}{" "}
                        {documents.length > 0 && `(${documents.length})`}
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {/* Debug Info */}
          {queryError && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-sm text-destructive font-medium">
                {t("admin.documents.loadError", "Error loading documents:")}
              </p>
              <p className="text-xs text-destructive/80 mt-1">
                {queryError.message}
              </p>
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t(
                  "admin.documents.searchPlaceholder",
                  "Search documents..."
                )}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(val: any) => setStatusFilter(val)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue
                  placeholder={t(
                    "admin.documents.filter.allStatuses",
                    "All Statuses"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("admin.documents.filter.allStatuses", "All Statuses")}
                </SelectItem>
                <SelectItem value="pending">
                  {t("admin.documents.status.pending", "Pending")}
                </SelectItem>
                <SelectItem value="approved">
                  {t("admin.documents.status.approved", "Approved")}
                </SelectItem>
                <SelectItem value="rejected">
                  {t("admin.documents.status.rejected", "Rejected")}
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue
                  placeholder={t(
                    "admin.documents.filter.allTypes",
                    "All Types"
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("admin.documents.filter.allTypes", "All Types")}
                </SelectItem>
                <SelectItem value="contract">
                  {t("admin.documents.docType.contract", "Contract")}
                </SelectItem>
                <SelectItem value="id_passport">
                  {t("admin.documents.docType.idPassport", "ID/Passport")}
                </SelectItem>
                <SelectItem value="proof_of_income">
                  {t(
                    "admin.documents.docType.proofOfIncome",
                    "Proof of Income"
                  )}
                </SelectItem>
                <SelectItem value="employment_letter">
                  {t(
                    "admin.documents.docType.employmentLetter",
                    "Employment Letter"
                  )}
                </SelectItem>
                <SelectItem value="reference">
                  {t("admin.documents.docType.reference", "Reference")}
                </SelectItem>
                <SelectItem value="other">
                  {t("admin.documents.docType.other", "Other")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredDocuments.length > 0 ? (
            <>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead className="font-semibold">
                        {t("admin.documents.table.document", "Document")}
                      </TableHead>
                      <TableHead className="font-semibold">
                        {t("admin.documents.table.tenant", "Tenant")}
                      </TableHead>
                      <TableHead className="font-semibold">
                        {t("admin.documents.table.type", "Type")}
                      </TableHead>
                      <TableHead className="font-semibold">
                        {t("admin.documents.table.status", "Status")}
                      </TableHead>
                      <TableHead className="font-semibold">
                        {t("admin.documents.table.size", "Size")}
                      </TableHead>
                      <TableHead className="font-semibold">
                        {t("admin.documents.table.uploaded", "Uploaded")}
                      </TableHead>
                      <TableHead className="text-right font-semibold">
                        {t("admin.documents.table.actions", "Actions")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedDocuments.map((doc: any) => (
                      <TableRow
                        key={doc.id}
                        className="hover:bg-slate-50/30 transition-colors"
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{doc.title}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {doc.profiles?.name ||
                                t(
                                  "admin.documents.screening.unknown",
                                  "Unknown"
                                )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {doc.profiles?.email ||
                                t("common.notAvailable", "N/A")}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-medium">
                            {doc.document_type}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(doc.status, doc)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatFileSize(doc.file_size)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateNumeric(doc.created_at, language)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {doc.file_url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDocument(doc)}
                                className="hover:bg-primary/10 hover:text-primary transition-colors"
                                title={t(
                                  "admin.documents.action.view",
                                  "View Document"
                                )}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleApproveDocument(doc)}
                              className={`transition-colors ${
                                doc.status === "approved"
                                  ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                  : "hover:bg-emerald-50 hover:text-emerald-700"
                              }`}
                              title={t(
                                "admin.documents.action.approve",
                                "Approve Document"
                              )}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRejectDocument(doc)}
                              className={`transition-colors ${
                                doc.status === "rejected"
                                  ? "bg-red-50 text-red-700 hover:bg-red-100"
                                  : "hover:bg-red-50 hover:text-red-700"
                              }`}
                              title={t(
                                "admin.documents.action.reject",
                                "Reject Document"
                              )}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                    className="gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    {t("admin.documents.pagination.previous", "Previous")}
                  </Button>
                  <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-sm font-semibold text-slate-700">
                      {currentPage}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="gap-2"
                  >
                    {t("admin.documents.pagination.next", "Next")}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground">
                {t("admin.documents.empty.title", "No documents found")}
              </p>
              {documentsData && documentsData.length === 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  {t(
                    "admin.documents.empty.hint",
                    "Try uploading a document or adjusting your filters"
                  )}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Document Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] w-full p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl font-bold">
              {viewingDocument?.title ||
                t("admin.documents.viewDialog.title", "View Document")}
            </DialogTitle>
            {viewingDocument?.profiles?.name && (
              <DialogDescription>
                {t("admin.documents.viewDialog.documentFor", "Document for")}{" "}
                {viewingDocument.profiles.name}
              </DialogDescription>
            )}
          </DialogHeader>
          {viewingDocument && (
            <div className="w-full h-[calc(90vh-140px)] flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-b-lg overflow-hidden p-6">
              {(() => {
                const fileType = getFileType(
                  viewingDocument.mime_type,
                  viewingDocument.file_name
                );

                if (fileType === "image") {
                  return (
                    <div className="w-full h-full flex items-center justify-center">
                      <img
                        src={viewingDocument.file_url}
                        alt={viewingDocument.title}
                        className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                        style={{ aspectRatio: "auto" }}
                      />
                    </div>
                  );
                } else if (fileType === "pdf") {
                  return (
                    <iframe
                      src={viewingDocument.file_url}
                      className="w-full h-full border-0 rounded-lg shadow-2xl bg-white"
                      title={viewingDocument.title}
                    />
                  );
                } else {
                  return (
                    <div className="flex flex-col items-center justify-center gap-4 p-8">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
                        <FileText className="w-10 h-10 text-slate-600" />
                      </div>
                      <p className="text-slate-600 font-medium">
                        {t(
                          "admin.documents.previewUnavailable",
                          "Preview not available for this file type"
                        )}
                      </p>
                      <Button
                        variant="outline"
                        onClick={() =>
                          window.open(viewingDocument.file_url, "_blank")
                        }
                        className="border-primary hover:bg-primary/10 hover:border-primary"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {t(
                          "admin.documents.downloadToView",
                          "Download to View"
                        )}
                      </Button>
                    </div>
                  );
                }
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-destructive" />
              {t("admin.documents.rejectDialog.title", "Reject Document")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.documents.rejectDialog.description",
                "Please provide a reason for rejecting this document. This will be visible to the tenant."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label
                htmlFor="rejection-reason"
                className="text-sm font-semibold"
              >
                {t(
                  "admin.documents.rejectDialog.reasonLabel",
                  "Rejection Reason *"
                )}
              </Label>
              <Textarea
                id="rejection-reason"
                placeholder={t(
                  "admin.documents.rejectDialog.reasonPlaceholder",
                  "Enter the reason for rejection..."
                )}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[120px] resize-none"
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false);
                setRejectionReason("");
                setRejectingDocumentId(null);
                setRejectingTenantUserId(null);
                setRejectingDocMeta(null);
              }}
              disabled={updateDocumentStatus.isPending}
            >
              {t("admin.documents.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleSubmitRejection}
              disabled={
                updateDocumentStatus.isPending || !rejectionReason.trim()
              }
              variant="destructive"
              className="gap-2"
            >
              {updateDocumentStatus.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("admin.documents.submitting", "Submitting...")}
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  {t("admin.documents.rejectDialog.title", "Reject Document")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Details Dialog - Luxury UI */}
      <Dialog
        open={rejectionDetailsOpen}
        onOpenChange={setRejectionDetailsOpen}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-destructive" />
              </div>
              {t("admin.documents.rejectionDetails.title", "Rejection Details")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.documents.rejectionDetails.description",
                "Document rejection information and reason"
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedRejectedDocument && (
            <div className="space-y-6 py-4">
              {/* Document Info Card */}
              <Card className="border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-white">
                <CardContent className="p-5">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        {t(
                          "admin.documents.rejectionDetails.documentTitle",
                          "Document Title"
                        )}
                      </Label>
                      <p className="text-lg font-semibold text-slate-900 mt-1">
                        {selectedRejectedDocument.title}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          {t("admin.documents.table.tenant", "Tenant")}
                        </Label>
                        <p className="text-sm font-medium text-slate-900 mt-1">
                          {selectedRejectedDocument.profiles?.name ||
                            t("admin.documents.screening.unknown", "Unknown")}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {selectedRejectedDocument.profiles?.email ||
                            t("common.notAvailable", "N/A")}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          {t(
                            "admin.documents.docTypeLabelPlain",
                            "Document Type"
                          )}
                        </Label>
                        <Badge variant="outline" className="mt-1">
                          {selectedRejectedDocument.document_type}
                        </Badge>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          {t(
                            "admin.documents.rejectionDetails.rejectedOn",
                            "Rejected On"
                          )}
                        </Label>
                        <p className="text-sm text-slate-700 mt-1">
                          {selectedRejectedDocument.reviewed_at
                            ? new Date(
                                selectedRejectedDocument.reviewed_at
                              ).toLocaleString(
                                language === "de" ? "de-DE" : "en-GB"
                              )
                            : t("common.notAvailable", "N/A")}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          {t(
                            "admin.documents.rejectionDetails.uploadedOn",
                            "Uploaded On"
                          )}
                        </Label>
                        <p className="text-sm text-slate-700 mt-1">
                          {new Date(
                            selectedRejectedDocument.created_at
                          ).toLocaleString(
                            language === "de" ? "de-DE" : "en-GB"
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Rejection Reason Card */}
              <Card className="border-2 border-destructive/20 bg-gradient-to-br from-red-50/50 to-white">
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                      <Info className="w-5 h-5 text-destructive" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label className="text-xs font-semibold text-destructive uppercase tracking-wide">
                        {t(
                          "admin.documents.rejectDialog.reasonLabelPlain",
                          "Rejection Reason"
                        )}
                      </Label>
                      <div className="p-4 bg-white rounded-lg border border-destructive/20 shadow-sm">
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {selectedRejectedDocument.notes ||
                            t(
                              "admin.documents.rejectionDetails.noReason",
                              "No reason provided"
                            )}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectionDetailsOpen(false)}
              className="w-full sm:w-auto"
            >
              {t("admin.documents.close", "Close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
